import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import PropTypes from 'prop-types';

import {getPercent, stringFormat} from '@ey/utils';

import {labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';
import {dashboardChartColors, dashboardTabId} from '../../../../../../util/uiconstants';
import normalizeDashboardFilters from '../../../../../../util/dashboard/normalizeDashboardFilters';

import OutputText from '@ey/canvascoreservices/OutputText';

import Tile from '../../../../../Common/Tile';
import PieChart from '../../../../../Common/Charts/PieChart';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../../actions/Metrics/dashboardFiltersAction';

const CCPSummaryView = ({engagementMetrics}) => {
	const [total, setTotal] = useState(0);
	const [totalReceived, setTotalReceived] = useState(0);
	const [totalAccepted, setTotalAccepted] = useState(0);
	const [totalOutstanding, setTotalOutstanding] = useState(0);
	const [totalDraft, setTotalDraft] = useState(0);
	const [totalOverdue, setTotalOverdue] = useState(0);
	const dispatch = useDispatch();

	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);

	useEffect(() => {
		if (engagementMetrics) {
			calculateCounts(engagementMetrics);
		}
	}, [engagementMetrics]);

	const handlerOnGraphicClick = (highChartEvent) => {
		const highChartPoint = highChartEvent.point;
		const getFilteredEngagemets = () => {
			switch (highChartEvent.point.options.name) {
				case labels.outstandingByEy:
					return engagementMetrics.filter((item) => item.cRCurDue > 0);
				case labels.receivedByEy:
					return engagementMetrics.filter((item) => item.cRReceived > 0);
				case labels.acceptedByEy:
					return engagementMetrics.filter((item) => item.cRAccepted > 0);
			}
		};

		const handlerOnClearFilter = () => {
			dispatch(clearFilterDashboardTiles(dashboardTabId.canvasClientPortal));
		};

		if (highChartPoint) {
			const dashboardFilters = normalizeDashboardFilters(
				[
					{
						categoryLabel: highChartEvent.point.options.name,
						onClearChip: handlerOnClearFilter
					}
				],
				getFilteredEngagemets(),
				handlerOnClearFilter
			);
			dispatch(updateFilterDashboardTiles(dashboardTabId.canvasClientPortal, dashboardFilters));
		}
	};

	const calculateCounts = (engagementsMetric) => {
		let recieved = 0;
		let accepted = 0;
		let total = 0;
		let overdue = 0;
		let draft = 0;

		for (let m = 0; m < engagementsMetric.length; m++) {
			const element = engagementsMetric[m];
			recieved = recieved + element.cRReceived;
			accepted = accepted + element.cRAccepted;
			total = total + element.cRTotal;
			if (element.cRCurDue) overdue = overdue + element.cRCurDue;
			draft = draft + element.cRNotSent;
		}
		setTotalReceived(recieved);
		setTotalAccepted(accepted);
		setTotalOutstanding(total - draft - recieved - accepted);
		setTotalOverdue(overdue);
		setTotalDraft(draft);
		setTotal(total - draft);
	};

	const getFooter = () => {
		if (totalDraft > 0)
			return (
				<OutputText body2 light>
					{stringFormat(labels.draftRequests, Utility.getFormattedNumber(numberFormat, totalDraft))}
				</OutputText>
			);
	};

	const pieProps = {
		labelCenterOffset: 25,
		legendWidth: 150,
		data: [
			{
				name: labels.outstandingByEy,
				y: getPercent(totalOutstanding, total),
				totalCount: totalOutstanding,
				totalOverdue: totalOverdue,
				color: dashboardChartColors.brand[0]
			},
			{
				name: labels.receivedByEy,
				y: getPercent(totalReceived, total),
				totalCount: totalReceived,
				totalOverdue: 0,
				color: dashboardChartColors.brand[1]
			},
			{
				name: labels.acceptedByEy,
				y: getPercent(totalAccepted, total),
				totalCount: totalAccepted,
				totalOverdue: 0,
				color: dashboardChartColors.brand[2]
			}
		],
		total: total,
		totalText: labels.totalRequests,
		onGraphicClick: handlerOnGraphicClick
	};

	const tileProps = {
		title: labels.requestsByStatus,
		footer: getFooter(),
		isEmpty: total === 0,
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: labels.selectadditionalEntitiesToContinue
	};

	return (
		<Tile {...tileProps}>
			<PieChart {...pieProps} />
		</Tile>
	);
};

CCPSummaryView.defaultProps = {
	engagementMetrics: []
};

CCPSummaryView.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default CCPSummaryView;
